import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProviders } from './context/AppProviders';
import brand from './config/brand';
import useJsonLd from './utils/useJsonLd';
import { getOrganizationJsonLd, getWebSiteJsonLd } from './utils/geo';
import { applyBrandMeta } from './utils/brandMeta';
import './styles/App.css';
import './styles/animations.css';
import './styles/CateringApp.css';
import CateringHeader from './components/catering/CateringHeader';
import SsoLand from './pages/SsoLand';
import Login from './pages/Account/Login';
import Register from './pages/Account/Register';
import { hospitality as hospitalityItems } from './context/items/hospitality';

// The catering item is static data. Passing it directly means the page renders
// the moment its chunk loads — no async category load, no /assets/content/*.md
// fetch (404s on this brand), and no "Item not found" flash.
const CATERING_ITEM = hospitalityItems.find((i) => i.shortTitle === 'catering');

// Keep each route in its own chunk so main.js stays small.
const CateringPage = lazy(() => import('./pages/Services/Hospitality/HospitalityDetailPage'));
const CateringDashboard = lazy(() => import('./pages/catering/CateringDashboard'));
const CateringCheckout = lazy(() => import('./pages/catering/CateringCheckout'));
const CateringPaymentSuccess = lazy(() => import('./pages/catering/CateringPaymentSuccess'));

const CateringFooter = () => (
  <footer className="ck-footer">
    <span>© {new Date().getFullYear()} {brand.siteName}</span>
    {brand.sister && (
      <a href={brand.sister.origin} className="ck-footer__link">{brand.sister.name}</a>
    )}
  </footer>
);

// chefkunming.com = focused mirror of mgv.gallery/hospitality/catering only.
const CateringApp = () => {
  // GEO: per-brand meta (title/canonical/og/twitter) so the chefkunming build is
  // attributed correctly even though it ships the shared static index.html.
  React.useEffect(() => { applyBrandMeta(brand); }, []);
  // GEO: brand-aware structured data (FoodService + WebSite) so AI crawlers can
  // identify the Chef Kunming entity. Mirrors the wiring in src/App.js.
  useJsonLd('mgv-organization', getOrganizationJsonLd(brand));
  useJsonLd('mgv-website', getWebSiteJsonLd(brand));
  return (
    <AppProviders>
      <Router basename={process.env.PUBLIC_URL || '/'}>
        <CateringHeader />
        <main className="ck-main">
          <Suspense fallback={<div className="ck-loading">Loading…</div>}>
            <Routes>
              <Route path="/" element={<CateringPage itemOverride={CATERING_ITEM} />} />
              <Route path="/hospitality/catering" element={<Navigate to="/" replace />} />
              <Route path="/dashboard" element={<CateringDashboard />} />
              <Route path="/checkout" element={<CateringCheckout />} />
              <Route path="/payment-success" element={<CateringPaymentSuccess />} />
              <Route path="/sso-land" element={<SsoLand />} />
              <Route path="/social-callback" element={<SsoLand />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <CateringFooter />
      </Router>
    </AppProviders>
  );
};

export default CateringApp;
